import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { apiUrl } from "./config";
import { useDocumentVisible } from "./useDocumentVisible";

export interface UseWorkspaceStreamOptions {
  workspaceId: string;
  /** Liga/desliga a conexão (ex.: só depois do primeiro GET carregar). */
  enabled: boolean;
  /**
   * Chamado quando o servidor sinaliza dado novo (`change`) ou quando a
   * conexão (re)abre (`hello`) — inclusive ao voltar de aba congelada.
   * O chamador decide como recarregar. Modal não interrompe prévia ao vivo.
   */
  onChange: () => void;
}

export interface WorkspaceStreamState {
  /** true = push ativo (a tela está "ao vivo", sem depender do polling). */
  connected: boolean;
}

/**
 * Sem NENHUMA atividade (hello/change/hb) neste intervalo, a conexão é
 * considerada morta e é recriada à força. Cobre o caso que o EventSource não
 * detecta sozinho: um proxy (vite/Caddy) segurando o socket aberto depois de
 * a API cair — sem watchdog, o front exibiria "Ao vivo" para sempre. O
 * servidor manda `hb` a cada 25s, então 60s = 2 heartbeats perdidos.
 */
const WATCHDOG_STALE_MS = 60_000;
const WATCHDOG_TICK_MS = 10_000;

/**
 * Assina o stream SSE do workspace (`/api/workspaces/:id/stream`).
 *
 * SSE porque o fluxo é unidirecional e o `EventSource` reconecta SOZINHO —
 * queda de rede ou restart da API terminam em reconexão automática. Aba oculta
 * fecha o stream; ao voltar, abre outro. O `hello` dispara `onChange` (refetch imediato,
 * sem esperar o próximo tick de polling). O polling de 5s vira fallback:
 * o chamador pode alargar o intervalo enquanto `connected === true`.
 */
export function useWorkspaceStream({
  workspaceId,
  enabled,
  onChange,
}: UseWorkspaceStreamOptions): WorkspaceStreamState {
  const visible = useDocumentVisible();
  const [connected, setConnected] = useState(false);
  // Nonce que força a recriação do EventSource quando o watchdog decide que a
  // conexão morreu silenciosamente.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  // Ref para o handler: eventos usam sempre a versão mais recente sem
  // reconectar o stream a cada render.
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!enabled || !visible || typeof window === "undefined" || !("EventSource" in window)) {
      return;
    }

    const source = new EventSource(
      apiUrl(`/api/workspaces/${encodeURIComponent(workspaceId)}/stream`),
    );
    let active = true;
    // A guarda cobre o evento recebido antes do cleanup e eventos já enfileirados
    // pelo browser mesmo depois de close (troca de aba/workflow/desmontagem).
    const acceptsEvent = () => active && document.visibilityState !== "hidden";
    let lastActivityAt = Date.now();
    const touch = () => {
      lastActivityAt = Date.now();
    };

    const handleSignal = () => {
      if (!acceptsEvent()) return;
      touch();
      onChangeRef.current();
    };
    // `hello` = conexão nova/reconexão → refetch para sincronizar já.
    source.addEventListener("hello", () => {
      if (!acceptsEvent()) return;
      setConnected(true);
      handleSignal();
    });
    source.addEventListener("change", handleSignal);
    // Heartbeat observável do servidor: só alimenta o watchdog.
    source.addEventListener("hb", () => {
      if (!acceptsEvent()) return;
      touch();
      setConnected(true);
    });
    source.onopen = () => {
      if (!acceptsEvent()) return;
      touch();
      setConnected(true);
    };
    // O EventSource reconecta sozinho após `onerror`; só refletimos o estado
    // para o fallback de polling encurtar o intervalo enquanto isso.
    source.onerror = () => { if (acceptsEvent()) setConnected(false); };

    // WATCHDOG: proxies podem manter o socket aberto com a API morta — sem
    // isso o EventSource nunca dispara erro e o "Ao vivo" mentiria. Cobre ainda
    // atraso de execução sem visibilitychange: o tick percebe o gap e recria
    // a conexão (que manda `hello` → refetch imediato).
    const watchdog = window.setInterval(() => {
      if (!acceptsEvent()) return;
      if (Date.now() - lastActivityAt > WATCHDOG_STALE_MS) {
        active = false;
        setConnected(false);
        source.close();
        setReconnectNonce((nonce) => nonce + 1);
      }
    }, WATCHDOG_TICK_MS);

    return () => {
      active = false;
      window.clearInterval(watchdog);
      source.close();
      setConnected(false);
    };
  }, [enabled, visible, workspaceId, reconnectNonce]);

  return { connected: enabled && visible && connected };
}
